// ─── PublicPortalPage — /public/:projectId (unauthenticated) ─────────────────
//
// Public-facing viewer served from the same React app the logged-in
// UI uses. Renders the project's shell — brand + Dashboard/Gallery
// tabs + timestamp + sites sidebar + main content — filtered to only
// the dashboards the project owner allowed in Settings → Public
// Portal.
//
// Fetches:
//   • /api/public-portal/<id>/         → { enabled, project, dashboards, allow_gallery, album_ids, default_dashboard_id }
//   • /api/public-portal/<id>/sites/   → same shape as /api/project/<id>/sites/ (unauth)
//
// Per-widget data rendering is a follow-up — the shell is here now so
// public visitors see the exact chrome they'll get, and widgets fill
// in as their public data endpoints land.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import axios from 'axios'
import {
  Loader, Lock, ChevronDown, ChevronRight, RefreshCw, Search, LayoutGrid, Image, Play,
} from 'lucide-react'
import { DashboardContext } from '../context/DashboardContext'
import { WidgetRenderer } from '../components/dashboard/DashboardWidgets'
import { DynamicGraphCard } from '../components/DynamicGraphCard'
import { CurrentConditionsView } from '../components/CurrentConditionsView'
import { SystemPerformanceView } from '../components/SystemPerformanceView'
import AllDataView from '../components/dashboard/AllDataView'

// Match the authed Parameter Comparison view's per-chart colors exactly
// (DashboardPage PARAM_CHART_COLORS) so the public view is identical.
const PARAM_CHART_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#3b82f6', '#ef4444', '#06b6d4']
import useAuthStore from '../store/auth-store'
import { projectsApi } from '../lib/projectsApi'
import { Navigation } from '../components/Navigation'
import { Footer } from '../components/Footer'
import {
  primeProject as primeWidgetConfigs,
  cacheGet as getWidgetConfig,
  isPrimed as widgetConfigsPrimed,
  onPrimed as onWidgetConfigsPrimed,
  PUBLIC_SELECTION_PREFIX,
  PUBLIC_SELECTION_WIDGET_ID,
} from '../lib/widgetConfigStore'

const API_URL       = import.meta.env.VITE_API_URL       || ''
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'

const now = () => new Date().toLocaleString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric',
  hour: 'numeric', minute: '2-digit',
  timeZoneName: 'short',
})

// Fallback default when the owner never synced a selection: the parameters of
// the first sensor (in site → device → sensor order) that has any, capped so a
// chatty sensor doesn't flood the chart. Keyed by the same `p.id` the sidebar
// checkboxes use.
function firstSensorParamIds(sites, cap = 4) {
  for (const site of (sites || []))
    for (const dev of (site.devices || []))
      for (const sensor of (dev.sensors || [])) {
        const ids = (sensor.parameters || []).map((p) => p.id).filter((v) => v != null)
        if (ids.length) return ids.slice(0, cap)
      }
  return []
}

export default function PublicPortalPage() {
  const { projectId } = useParams()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const currentUser     = useAuthStore((s) => s.user)

  // Access probe — signed-in users with project access get sent to
  // the full authenticated dashboard. Anonymous visitors and signed-in
  // users without access fall through to the public shell.
  //
  // `fetchProjectSites` is the canonical access gate — it 403s any
  // user who isn't the owner, admin, or a member of the project.
  // (fetchProject only checks the row exists, so we can't use it.)
  // Redirect only when the visitor actually has access to the
  // project — signed-in users who aren't project members stay on
  // the public shell (which renders widgets via the public
  // graph-data endpoint).
  const looksSignedIn = isAuthenticated || !!currentUser

  const [accessCheck, setAccessCheck] = useState({ done: false, hasAccess: false })
  useEffect(() => {
    if (!projectId) {
      setAccessCheck({ done: true, hasAccess: false })
      return
    }
    if (!looksSignedIn) {
      setAccessCheck({ done: true, hasAccess: false })
      return
    }
    // Signed-in — probe the sites endpoint. 200 means the token/
    // session was accepted AND the user is on the project's ACL.
    // 401 / 403 / anything else → stay on public.
    let cancelled = false
    projectsApi.fetchProjectSites(Number(projectId))
      .then((res) => {
        if (cancelled) return
        // fetchProjectSites returns:
        //   * success:  { success:true, sites:[…], project_id, total_sites }
        //   * failure:  [] (empty array — caller swallows the error)
        const sites = Array.isArray(res?.sites) ? res.sites : []
        const ok = !!(res && (res.success === true || sites.length > 0))
        setAccessCheck({ done: true, hasAccess: ok })
      })
      .catch(() => { if (!cancelled) setAccessCheck({ done: true, hasAccess: false }) })
    return () => { cancelled = true }
  }, [projectId, looksSignedIn])

  const [config, setConfig]   = useState(null)
  const [sites,  setSites]    = useState([])
  const [loading, setLoading] = useState(true)
  const [error,  setError]    = useState(null)

  // Which dashboard is currently active + is the Gallery pane open?
  const [activeDashboardId, setActiveDashboardId] = useState(null)
  const [galleryOpen,       setGalleryOpen]       = useState(false)
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false)
  const [expandedSites,     setExpandedSites]     = useState({})
  const [expandedDevices,   setExpandedDevices]   = useState({})
  const [expandedSensors,   setExpandedSensors]   = useState({})
  const [checkedParams,     setCheckedParams]     = useState({})
  const [siteSearch,        setSiteSearch]        = useState('')
  const [asOfLabel,         setAsOfLabel]         = useState(now())

  // Prime the shared widget-config cache — brings the owner's saved
  // Gauge/Bullet/Solid/Webcam/Gallery/Support picks onto this page
  // via the /api/dashboard/widget-configs/ endpoint.
  useEffect(() => {
    if (!projectId) return
    primeWidgetConfigs(projectId)
  }, [projectId])

  // Seed the sidebar so chart widgets populate on load instead of rendering
  // blank. Preference order:
  //   1) The owner's mirrored selection (widget-config cache), when present.
  //   2) Fallback: the first sensor's parameters from the shared sites — so a
  //      project whose owner never synced a selection still shows real data.
  // Runs once per project, only when the visitor hasn't ticked anything, and
  // only after both `sites` and the widget-config prime are available.
  const seededSelectionRef = useRef(false)
  useEffect(() => {
    if (seededSelectionRef.current || !projectId || !sites.length) return
    const seed = () => {
      if (seededSelectionRef.current || !sites.length) return
      // Every parameter id that actually exists in the shared sites.
      const validIds = new Set()
      for (const site of sites)
        for (const dev of (site.devices || []))
          for (const sensor of (dev.sensors || []))
            for (const p of (sensor.parameters || [])) validIds.add(p.id)

      // 1) Owner's mirrored selection, filtered to params still shared.
      const sel = getWidgetConfig(PUBLIC_SELECTION_PREFIX, projectId, PUBLIC_SELECTION_WIDGET_ID)
      let ids = (Array.isArray(sel?.parameterIds) ? sel.parameterIds : []).filter((id) => validIds.has(id))
      // 2) Fallback to the first non-empty sensor's parameters.
      if (!ids.length) ids = firstSensorParamIds(sites)
      if (!ids.length) return

      seededSelectionRef.current = true
      setCheckedParams((prev) => {
        if (Object.keys(prev).length) return prev   // visitor already interacted
        const next = {}
        ids.forEach((id) => { next[id] = true })
        return next
      })
    }
    if (widgetConfigsPrimed(projectId)) { seed(); return }
    return onWidgetConfigsPrimed((pid) => { if (pid === String(projectId)) seed() })
  }, [projectId, sites])

  // Initial load — config + sites in parallel.
  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    setLoading(true); setError(null)
    Promise.all([
      axios.get(`${API_URL}${API_BASE_PATH}/public-portal/${projectId}/`),
      axios.get(`${API_URL}${API_BASE_PATH}/public-portal/${projectId}/sites/`).catch(() => null),
    ])
      .then(([cfgRes, sitesRes]) => {
        if (cancelled) return
        const cfg = cfgRes.data || {}
        setConfig(cfg)
        setSites(sitesRes?.data?.data || [])
        // Default dashboard picks the configured one, else the first allowed.
        const defId = cfg.default_dashboard_id || cfg.dashboards?.[0]?.id
        setActiveDashboardId(defId || null)
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load public portal') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectId])

  const projectName = config?.project?.name || `Project #${projectId}`
  const dashboards  = config?.dashboards    || []
  const showGallery = !!config?.allow_gallery
  const activeDash  = useMemo(
    () => dashboards.find((d) => d.id === activeDashboardId) || dashboards[0] || null,
    [dashboards, activeDashboardId],
  )

  // ── DashboardContext state ─────────────────────────────────────────────
  // Widget components (LineChart, Wind Rose, gauges, panels) all read
  // `selectedParameters` + date range from this context. Build the
  // parameter list from the sidebar checkboxes so charts respond to
  // the visitor's selection like they do on the authed dashboard.
  const selectedParameters = useMemo(() => {
    const out = []
    for (const site of sites) {
      for (const dev of (site.devices || [])) {
        for (const sensor of (dev.sensors || [])) {
          for (const p of (sensor.parameters || [])) {
            if (checkedParams[p.id]) {
              out.push({
                id:         p.id,
                name:       p.name,
                unit:       p.unit || '',
                siteName:   site.name || '',
                deviceName: dev.name || '',
                sensorName: sensor.name || '',
                parameter_id: p.parameter_id,
              })
            }
          }
        }
      }
    }
    return out
  }, [sites, checkedParams])

  const [activePeriod, setActivePeriod] = useState('D')
  const [dateRange,    setDateRange]    = useState(() => {
    // Default to last 24h — matches the "D" period from the authed side.
    const to   = new Date()
    const from = new Date(Date.now() - 24 * 60 * 60 * 1000)
    const fmt  = (d) => d.toISOString().replace('T', ' ').slice(0, 19)
    return { from: fmt(from), to: fmt(to) }
  })
  const handlePeriodClick = (p) => {
    setActivePeriod(p)
    const to  = new Date()
    const map = { D: 1, W: 7, M: 30, Y: 365 }
    const days = map[p] || 1
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    const fmt = (d) => d.toISOString().replace('T', ' ').slice(0, 19)
    setDateRange({ from: fmt(from), to: fmt(to) })
  }

  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase()
    if (!q) return sites
    return sites
      .map((s) => {
        // Filter device/sensor/param names against the search term but
        // keep the site if any descendant matches.
        const devices = (s.devices || []).map((d) => ({
          ...d,
          sensors: (d.sensors || []).map((se) => ({
            ...se,
            parameters: (se.parameters || []).filter((p) => (p.name || '').toLowerCase().includes(q)),
          })).filter((se) => (se.name || '').toLowerCase().includes(q) || se.parameters.length),
        })).filter((d) => (d.name || '').toLowerCase().includes(q) || d.sensors.length)
        return { ...s, devices }
      })
      .filter((s) => (s.name || '').toLowerCase().includes(q) || s.devices.length)
  }, [sites, siteSearch])

  // ─── Access + Loading / disabled states ───────────────────────────────
  // Signed-in users with access → send them straight to the authed
  // dashboard view. `replace` so Back doesn't ping-pong into /public.
  if (accessCheck.done && accessCheck.hasAccess) {
    return <Navigate to={`/dashboard/project/${projectId}`} replace />
  }
  // Probe hasn't resolved yet — hold before rendering anything so
  // we don't briefly flash the public shell and then swap to authed.
  if (!accessCheck.done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-500">
        <Loader className="w-4 h-4 animate-spin mr-2" /> Checking access…
      </div>
    )
  }
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-500">
        <Loader className="w-4 h-4 animate-spin mr-2" /> Loading public portal…
      </div>
    )
  }
  if (!config || config.enabled === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation hideMarketingBar />
        <div className="flex-1 max-w-3xl mx-auto p-6 w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <Lock className="w-8 h-8 mx-auto text-gray-400 mb-3" />
            <h1 className="text-xl font-bold text-gray-800 mb-1">{projectName}</h1>
            <p className="text-sm text-gray-600">
              This project&apos;s public portal is not currently available.
              The project owner may enable it from
              <span className="whitespace-nowrap"> Settings → Public Portal</span>.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation hideMarketingBar />
        <div className="flex-1 max-w-3xl mx-auto p-6 w-full">
          <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700">{error}</div>
        </div>
        <Footer />
      </div>
    )
  }

  // ─── Full shell ───────────────────────────────────────────────────────
  return (
    <DashboardContext.Provider value={{
      selectedParameters,
      sites,
      projectId: Number(projectId),
      dateRange,
      activePeriod,
      handlePeriodClick,
      setDateRange,
      // Public portal is browse-only. Widgets now render their pencil ONLY
      // in Edit Mode (`isCustomizing && canEdit`), so false is what hides
      // them here. This used to be `true` — a deliberate inversion of the
      // old `!isCustomizing` rule — and leaving it that way after the rule
      // flipped would have shown edit pencils to anonymous visitors.
      // The three-dot menu stays: it only exposes read-only Download/View
      // actions, which visitors are allowed to use.
      isCustomizing: false,
      goToGallery: () => { setGalleryOpen(true); setDashboardMenuOpen(false) },
    }}>
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Site-wide top navigation — same one every authenticated page
          uses. Handles Sign In / Messages / Help Center / nexsens.com
          consistently for public visitors + shows a UserDropdown for
          signed-in visitors. */}
      <Navigation hideMarketingBar />

      {/* Brand + Dashboard/Gallery tabs + "As of ..." + refresh —
          same shape as the authenticated dashboard's AppHeader but
          only the two menus the public portal exposes. */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[26px] py-3 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2">
            <img src="/images/dashboardlogo.png" alt="Fondriest" className="h-10 w-auto"
                 onError={(e) => { e.currentTarget.src = '/images/logo.png' }} />
          </a>

          <nav className="flex items-center gap-6 text-sm font-semibold text-gray-700">
            {dashboards.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setDashboardMenuOpen((v) => !v)}
                  className={`inline-flex items-center gap-1 border-b-2 pb-1 ${
                    !galleryOpen ? 'border-[#358ECD] text-[#358ECD]' : 'border-transparent hover:text-[#358ECD]'
                  }`}
                >
                  Dashboard <ChevronDown className="w-4 h-4" />
                </button>
                {dashboardMenuOpen && (
                  <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 min-w-[220px] py-1">
                    {dashboards.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => { setActiveDashboardId(d.id); setGalleryOpen(false); setDashboardMenuOpen(false) }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                          d.id === activeDashboardId ? 'text-[#358ECD] font-semibold' : 'text-gray-700'
                        }`}
                      >
                        {d.name}
                        {d.id === config.default_dashboard_id && (
                          <span className="ml-2 text-[10px] uppercase text-gray-400">Default</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {showGallery && (
              <button
                onClick={() => { setGalleryOpen(true); setDashboardMenuOpen(false) }}
                className={`inline-flex items-center gap-1 border-b-2 pb-1 ${
                  galleryOpen ? 'border-[#358ECD] text-[#358ECD]' : 'border-transparent hover:text-[#358ECD]'
                }`}
              >
                Gallery
              </button>
            )}
          </nav>

          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>As of {asOfLabel}</span>
            <button
              type="button"
              onClick={() => setAsOfLabel(now())}
              title="Refresh"
              className="p-1.5 text-[#358ECD] hover:bg-gray-100 rounded"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Body — sidebar + main */}
      <div className="flex-1 flex">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 bg-white border-r border-gray-200">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-800">All Sites</h2>
          </div>
          <div className="px-3 py-3">
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
                placeholder="Search"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#358ECD]"
              />
            </div>

            {filteredSites.length === 0 && (
              <p className="text-xs text-gray-500 italic px-1">No sites shared publicly yet.</p>
            )}

            {/* Mirror the authed sidebar shape exactly:
                Site      → bold heading, always shown
                Device    → bold sub-heading, always shown, "<uid> Data Logger" suffix
                Sensor    → checkbox + chevron, collapsible parameter list
                Parameter → checkbox, indented under sensor
                See DashboardPage.jsx "Normal View" render block. */}
            <div className="space-y-1.5 text-sm">
              {filteredSites.map((site) => (
                <div key={site.id} className="space-y-1.5">
                  <h3 className="pt-2 text-[15px] font-bold text-gray-900 leading-tight">
                    {site.name}
                  </h3>
                  {(site.devices || []).map((dev) => (
                    <div key={dev.id} className="space-y-1">
                      <h4 className="pt-1 text-[12px] font-bold text-gray-800 leading-tight">
                        {/Data Logger$/i.test(dev.name || '')
                          ? dev.name
                          : `${dev.name || `Device ${dev.id}`} Data Logger`}
                      </h4>
                      {(dev.sensors || []).map((sensor) => {
                        const sensKey = `se-${site.id}-${dev.id}-${sensor.id}`
                        const sensOpen = !!expandedSensors[sensKey]
                        // "Sensor checkbox" toggles every parameter under it.
                        const paramIds = (sensor.parameters || []).map((p) => p.id)
                        const someOn   = paramIds.some((id) => checkedParams[id])
                        const allOn    = paramIds.length > 0 && paramIds.every((id) => checkedParams[id])
                        const toggleSensor = () => {
                          setCheckedParams((c) => {
                            const next = { ...c }
                            const target = !allOn
                            paramIds.forEach((id) => { next[id] = target })
                            return next
                          })
                        }
                        return (
                          <div key={sensor.id}>
                            <button
                              onClick={() => setExpandedSensors((e) => ({ ...e, [sensKey]: !sensOpen }))}
                              className="w-full flex items-center gap-2 py-1 text-[13px] text-gray-700 hover:bg-gray-50 rounded transition"
                            >
                              <input
                                type="checkbox"
                                checked={allOn}
                                ref={(el) => { if (el) el.indeterminate = someOn && !allOn }}
                                onChange={toggleSensor}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                              />
                              <span className="flex-1 text-left truncate">{sensor.name}</span>
                              {paramIds.length > 0 && (
                                <ChevronDown
                                  className={`w-3.5 h-3.5 text-gray-500 transition-transform flex-shrink-0 mr-1 ${
                                    sensOpen ? 'rotate-180' : ''
                                  }`}
                                />
                              )}
                            </button>
                            {sensOpen && (sensor.parameters || []).length > 0 && (
                              <div className="ml-6 space-y-0">
                                {sensor.parameters.map((p) => (
                                  <button
                                    key={p.id}
                                    onClick={() => setCheckedParams((c) => ({ ...c, [p.id]: !c[p.id] }))}
                                    className="w-full flex items-center gap-2 py-1 text-[13px] text-gray-600 hover:bg-gray-50 rounded transition"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!checkedParams[p.id]}
                                      onChange={() => setCheckedParams((c) => ({ ...c, [p.id]: !c[p.id] }))}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                    />
                                    <span className="truncate text-left flex-1 font-normal">{p.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 min-w-0">
          {galleryOpen ? (
            <GalleryPane projectId={projectId} albumIds={config.album_ids || []} />
          ) : activeDash ? (
            <DashboardPane
              projectId={projectId}
              dashboard={activeDash}
              sites={sites}
              selectedParameters={selectedParameters}
              dateRange={dateRange}
              activePeriod={activePeriod}
              onPeriodClick={handlePeriodClick}
              onDateChange={(field, value) => setDateRange((prev) => ({ ...prev, [field]: value }))}
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-sm text-gray-500">
              No dashboards enabled for public viewing.
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
    </DashboardContext.Provider>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function DashboardPane({ projectId, dashboard, sites = [], selectedParameters = [], dateRange = {}, activePeriod = 'D', onPeriodClick, onDateChange }) {
  // The authed dashboard renders several views with bespoke components rather
  // than a template widget grid. Render those the SAME way here so the public
  // view is identical instead of falling back to the generic template widgets.
  const isParamComparison =
    dashboard?.template_type === 'parameter_comparison' ||
    dashboard?.name === 'Parameter Comparison'
  const isCurrentConditions =
    dashboard?.template_type === 'current_conditions' ||
    dashboard?.name === 'Current Conditions'
  const isSystemPerformance =
    dashboard?.template_type === 'system_performance' ||
    dashboard?.name === 'System Performance'
  const isAllData =
    dashboard?.template_type === 'all_data' ||
    dashboard?.name === 'All Data'
  // Fetch the widget layout from the unauthenticated public endpoint.
  // Returns the same shape as /api/dashboard/templates/<id>/layout/ so
  // the widget-rendering component can drive both views.
  const [layout, setLayout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!projectId || !dashboard?.id) return
    let cancelled = false
    setLoading(true); setError(null); setLayout(null)
    axios.get(`${API_URL}${API_BASE_PATH}/public-portal/${projectId}/dashboard/${dashboard.id}/layout/`)
      .then((res) => { if (!cancelled) setLayout(res.data) })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load layout') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectId, dashboard?.id])

  const widgets     = layout?.widgets || []
  const cols        = layout?.layout_config?.cols      || 12
  const rowHeight   = layout?.layout_config?.row_height || 100
  const gap         = layout?.layout_config?.gap        || 8

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <LayoutGrid className="w-4 h-4 text-gray-500" />
        <h2 className="text-sm font-bold text-gray-800">{dashboard.name}</h2>
      </div>
      <div className="p-4">
        {isParamComparison ? (
          <DynamicGraphCard
            title="Graph"
            selectedParameters={selectedParameters}
            dateRange={dateRange}
            timezone="UTC"
            activePeriod={activePeriod}
            onPeriodClick={onPeriodClick}
            onDateChange={onDateChange}
            storageKey="wq_public_param_comparison_graph"
            colors={PARAM_CHART_COLORS}
            editable={false}
            emptyMessage="Select one or more parameters from the sidebar to compare them here."
          />
        ) : isCurrentConditions ? (
          <CurrentConditionsView
            projectId={projectId}
            sites={sites}
            selectedParameters={selectedParameters}
            dateRange={dateRange}
            timezone="UTC"
            projectImage={null}
          />
        ) : isSystemPerformance ? (
          <SystemPerformanceView
            projectId={projectId}
            sites={sites}
            selectedParameters={selectedParameters}
            dateRange={dateRange}
            timezone="UTC"
            activePeriod={activePeriod}
            onPeriodClick={onPeriodClick}
            onDateChange={onDateChange}
          />
        ) : isAllData ? (
          <AllDataView selectedParameters={selectedParameters} projectId={projectId} />
        ) : (
        <>
        {loading && (
          <div className="text-center text-sm text-gray-500 py-10">
            <Loader className="w-4 h-4 animate-spin inline mr-2" /> Loading widgets…
          </div>
        )}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">{error}</div>
        )}
        {!loading && !error && widgets.length === 0 && (
          <div className="border-2 border-dashed border-gray-200 rounded p-8 text-center text-sm text-gray-500">
            This dashboard has no widgets yet.
          </div>
        )}
        {!loading && !error && widgets.length > 0 && (
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              // `minmax(rowHeight, auto)` — each row is at least the
              // configured base but can grow to fit its widget's
              // natural height. Fixed-height rows were clipping the
              // widget bodies and forcing per-widget scrollbars.
              gridAutoRows: `minmax(${rowHeight}px, auto)`,
              gap: `${gap}px`,
            }}
          >
            {widgets.map((w) => (
              <div
                key={w.id}
                style={{
                  gridColumn: `${(w.col || 0) + 1} / span ${w.cols || 1}`,
                  gridRow:    `${(w.row || 0) + 1} / span ${w.rows || 1}`,
                }}
              >
                {/* Reuse the same WidgetRenderer the authed dashboard
                    uses. Chart / gauge / panel widgets pull data from
                    /api/data/graph-data/ which is already public
                    (AllowAny). isEditing=false and no edit/delete
                    callbacks means read-only display. */}
                <WidgetRenderer widget={w} isEditing={false} />
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  )
}

function GalleryPane({ projectId, albumIds }) {
  // Album metadata for the section titles. Fetches the project's
  // full album list once, then filters to the ids the owner allowed
  // in Settings → Public Portal. Skips albums that aren't real
  // (legacy config still carries string placeholders like
  // "site-conditions" from an earlier hard-coded list).
  const [albums,  setAlbums]  = useState([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [lightbox, setLightbox] = useState(null)   // {albumName, photos, index}

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    setLoading(true); setError(null)
    axios.get(`${API_URL}${API_BASE_PATH}/photo/album/?project=${projectId}&page_size=500`)
      .then((res) => {
        if (cancelled) return
        const raw = res.data?.results || res.data?.data || res.data || []
        const rows = Array.isArray(raw) ? raw : []
        setAlbums(rows.map((a) => ({ id: String(a.id), name: a.name || `Album #${a.id}` })))
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load albums') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectId])

  // Filter down to the albums the owner allowed. Cast both sides to
  // string so numeric ids saved from the settings modal still match.
  const allowedIds  = useMemo(() => new Set((albumIds || []).map(String)), [albumIds])
  const shownAlbums = albums.filter((a) => allowedIds.has(String(a.id)))

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Image className="w-4 h-4 text-gray-500" />
        <h2 className="text-sm font-bold text-gray-800">Gallery</h2>
      </div>
      <div className="p-6 space-y-8">
        {loading && (
          <div className="text-center text-sm text-gray-500 py-6">
            <Loader className="w-4 h-4 animate-spin inline mr-2" /> Loading albums…
          </div>
        )}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">{error}</div>
        )}
        {!loading && !error && shownAlbums.length === 0 && (
          <p className="text-sm text-gray-500 italic">No albums shared publicly.</p>
        )}
        {shownAlbums.map((a) => (
          <AlbumSection
            key={a.id}
            album={a}
            onOpen={(photos, index) => setLightbox({ albumName: a.name, photos, index })}
          />
        ))}
      </div>
      {lightbox && (
        <GalleryLightbox
          albumName={lightbox.albumName}
          photos={lightbox.photos}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}

// One album row — title + responsive media grid. Pulls BOTH the album's photos
// (`/api/photo/photo/?album=<id>`) and its videos (`/api/photo/video/?album=<id>`)
// — both endpoints are anonymous-safe and support ?album=. Photos and videos are
// merged into one grid; a video tile shows its poster (thumbnail_url) with a play
// badge and plays inline in the lightbox.
// How many media tiles to show per page within an album (photos + videos combined).
const GALLERY_PAGE_SIZE = 12

function AlbumSection({ album, onOpen }) {
  const [items,   setItems]   = useState([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [page,    setPage]    = useState(1)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    const toList = (res) => {
      const raw = res?.data?.results || res?.data?.data || res?.data || []
      return Array.isArray(raw) ? raw : []
    }
    Promise.all([
      axios.get(`${API_URL}${API_BASE_PATH}/photo/photo/?album=${album.id}&page_size=200`),
      axios.get(`${API_URL}${API_BASE_PATH}/photo/video/?album=${album.id}&page_size=200`)
        .catch(() => ({ data: [] })),   // videos are optional — a photo-only album still renders
    ])
      .then(([photoRes, videoRes]) => {
        if (cancelled) return
        const photos = toList(photoRes).map((p, i) => ({
          id:      `p${p.id ?? i}`,
          type:    'photo',
          src:     p.image || p.photo_url || p.url || p.file_url || p.path || '',
          caption: p.name || p.title || `Photo ${i + 1}`,
        })).filter((m) => m.src)
        const videos = toList(videoRes).map((v, i) => ({
          id:      `v${v.id ?? i}`,
          type:    'video',
          src:     v.video_url || v.url || v.path || '',
          poster:  v.thumbnail_url || '',
          caption: v.name || v.title || `Video ${i + 1}`,
        })).filter((m) => m.src)
        setItems([...photos, ...videos])
        setPage(1)   // reset to the first page whenever the album (re)loads
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load media') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [album.id])

  // Client-side paging so a large album isn't dumped onto one page. The lightbox
  // still gets the FULL item list + the clicked tile's GLOBAL index, so prev/next
  // there walks the whole album, not just the visible page.
  const totalPages = Math.max(1, Math.ceil(items.length / GALLERY_PAGE_SIZE))
  const safePage   = Math.min(page, totalPages)
  const pageStart  = (safePage - 1) * GALLERY_PAGE_SIZE
  const pageItems  = items.slice(pageStart, pageStart + GALLERY_PAGE_SIZE)

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-800 mb-3">{album.name}</h3>
      {loading && (
        <div className="text-center text-xs text-gray-500 py-4">
          <Loader className="w-3.5 h-3.5 animate-spin inline mr-1" /> Loading media…
        </div>
      )}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">{error}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="text-xs text-gray-500 italic">This album has no photos or videos yet.</p>
      )}
      {items.length > 0 && (
        <>
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {pageItems.map((m, idx) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => onOpen(items, pageStart + idx)}
                className="relative block w-full aspect-square overflow-hidden rounded border border-gray-200 hover:border-[#358ECD] transition group"
                title={m.caption}
              >
                {m.type === 'video' && m.poster ? (
                  <img
                    src={m.poster}
                    alt={m.caption}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3' }}
                  />
                ) : m.type === 'video' ? (
                  // No poster — show the first frame via a muted, non-preloading <video>.
                  <video
                    src={m.src}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover bg-black"
                  />
                ) : (
                  <img
                    src={m.src}
                    alt={m.caption}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3' }}
                  />
                )}
                {m.type === 'video' && (
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="bg-black/50 rounded-full p-2.5 group-hover:bg-black/70 transition">
                      <Play className="w-5 h-5 text-white fill-white" />
                    </span>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {totalPages > 1 && (
          <GalleryPager page={safePage} totalPages={totalPages} onPage={setPage} />
        )}
        </>
      )}
    </section>
  )
}

// Compact pager for an album's media grid — Prev / windowed page numbers / Next.
function GalleryPager({ page, totalPages, onPage }) {
  const span = 2   // page numbers shown on each side of the current page
  const from = Math.max(1, page - span)
  const to   = Math.min(totalPages, page + span)
  const nums = []
  for (let n = from; n <= to; n++) nums.push(n)
  const base = 'min-w-[28px] h-7 px-2 rounded text-xs border transition'
  const plain = `${base} border-gray-200 text-gray-600 hover:border-[#358ECD]`
  const step = `${plain} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200`
  return (
    <nav className="mt-4 flex flex-wrap items-center justify-center gap-1" aria-label="Gallery pages">
      <button type="button" className={step} onClick={() => onPage(page - 1)} disabled={page <= 1} title="Previous page">‹</button>
      {from > 1 && (
        <>
          <button type="button" className={plain} onClick={() => onPage(1)}>1</button>
          {from > 2 && <span className="px-1 text-xs text-gray-400">…</span>}
        </>
      )}
      {nums.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onPage(n)}
          aria-current={n === page ? 'page' : undefined}
          className={n === page ? `${base} border-[#358ECD] bg-[#358ECD] text-white` : plain}
        >{n}</button>
      ))}
      {to < totalPages && (
        <>
          {to < totalPages - 1 && <span className="px-1 text-xs text-gray-400">…</span>}
          <button type="button" className={plain} onClick={() => onPage(totalPages)}>{totalPages}</button>
        </>
      )}
      <button type="button" className={step} onClick={() => onPage(page + 1)} disabled={page >= totalPages} title="Next page">›</button>
    </nav>
  )
}

// Full-screen lightbox with prev/next + Escape-to-close.
function GalleryLightbox({ albumName, photos, startIndex = 0, onClose }) {
  const [i, setI] = useState(startIndex)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft')  setI((v) => (v - 1 + photos.length) % photos.length)
      if (e.key === 'ArrowRight') setI((v) => (v + 1) % photos.length)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [photos.length, onClose])
  const cur = photos[i]
  if (!cur) return null
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-6"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl leading-none"
        onClick={onClose}
        title="Close"
      >×</button>
      <div className="text-white/70 text-xs mb-2">{albumName} · {i + 1} of {photos.length}</div>
      {cur.type === 'video' ? (
        <video
          key={cur.id}
          src={cur.src}
          poster={cur.poster || undefined}
          controls
          autoPlay
          playsInline
          className="max-h-[80vh] max-w-[90vw] shadow-2xl bg-black"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={cur.src}
          alt={cur.caption}
          className="max-h-[80vh] max-w-[90vw] object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <div className="text-white/80 text-sm mt-3">{cur.caption}</div>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setI((v) => (v - 1 + photos.length) % photos.length) }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-3xl px-3 py-2"
            title="Previous"
          >‹</button>
          <button
            onClick={(e) => { e.stopPropagation(); setI((v) => (v + 1) % photos.length) }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-3xl px-3 py-2"
            title="Next"
          >›</button>
        </>
      )}
    </div>
  )
}
